import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cva } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '@april9au/react-common/utils';

const buttonVariants = cva(
  // Every button answers a press. On a phone the tap highlight is off
  // (`app.css`), so without this a control gives no feedback at all until the
  // navigation lands; a 2 % squeeze is the cheapest honest acknowledgement and
  // it is transform-only, so it neither repaints nor shifts anything.
  // The default focus ring is blue, not `--ring`. `--ring` is SLSF yellow,
  // which measures 1.45:1 on white — an indicator nobody can see is not an
  // indicator. Blue is the app's wayfinding colour and reads on every surface
  // the buttons land on; the variants that need a different one say so.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,background-color,border-color,box-shadow,scale] duration-150 active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-none focus-visible:ring-surf-blue/40 focus-visible:ring-[3px] aria-invalid:ring-bp-destructive/40 aria-invalid:border-bp-destructive",
  {
    variants: {
      /*
        Every variant names *both* its ground and its ink, in every state it
        defines. That rule is the whole of ticket 23 §3: the registry copy of
        `outline` and `ghost` changed only the ground on hover and left the ink
        to `--accent-foreground` (white) or to whatever the surrounding
        document happened to set, which is how the Start-over dialog ended up
        painting near-white text on a pale pink ground the moment a pointer
        touched it. A variant that never hands its ink to an ancestor cannot
        do that, whichever surface it is dropped on.

        Measured against white (`#fff`) and against the navy frosted panel;
        the numbers are in the ticket-23 report. Nothing below is under 4.5:1.
      */
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 active:bg-primary/80',
        /**
         * Blue chrome action: onboarding, header and menu. `primary` is SLSF
         * yellow/red depending on the theme block, and red is reserved for
         * destructive actions, so positive chrome actions use this.
         */
        brand:
          'bg-surf-blue text-white shadow-xs hover:bg-surf-blue/90 focus-visible:ring-surf-blue/40 disabled:hover:bg-surf-blue',
        /**
         * The one primary action on an onboarding screen: SLSF yellow with
         * navy ink. Hover and pressed step *down* in lightness, so the button
         * darkens under the finger rather than glowing, and the focus ring is
         * navy inside a white halo — the only pairing that stays visible on
         * the frosted card and on the photograph both.
         */
        'brand-yellow':
          'bg-bp-yellow text-bp-on-yellow shadow-[0_12px_28px_-14px_hsl(var(--surf-dark-blue)/0.8)] transition-[color,background-color,box-shadow] hover:bg-bp-yellow-hover active:bg-bp-yellow-pressed focus-visible:ring-0 focus-visible:shadow-[var(--bp-focus-ring)] disabled:hover:bg-bp-yellow',
        /**
         * A filled destructive action. `--bp-destructive` rather than
         * `--destructive`: the brand red is 4.30:1 under white text, which is
         * a fail, and the chat needs the brand red for other things.
         */
        destructive:
          'bg-bp-destructive text-white shadow-xs hover:bg-bp-destructive-hover active:bg-bp-destructive-pressed focus-visible:ring-bp-destructive/40 disabled:hover:bg-bp-destructive',
        /**
         * The destructive action when it must not out-shout the safe one — the
         * Start-over confirmation is the only place. Red border and red ink at
         * rest; hover and pressed *fill* with that same red and flip the ink to
         * white. Filling rather than tinting is the choice ticket 23 asked for,
         * and it is the one that cannot go wrong: there is no intermediate
         * ground for the label to get lost against.
         *
         * Rest and filled both measure 5.4:1, so the two states are legible on
         * the same terms rather than one being a weaker version of the other.
         *
         * The ground is opaque white rather than `transparent`. Over the
         * dialog it is the surface's own colour, so it changes nothing where
         * the variant is actually used; anywhere darker, transparency put the
         * red ink at 1.7:1 and even `white/80` only reached 3.9:1. Red is a
         * narrow enough ink that it has to bring its own ground.
         */
        'destructive-outline':
          'border-bp-destructive text-bp-destructive border-2 bg-white hover:bg-bp-destructive hover:text-white active:bg-bp-destructive-pressed active:text-white focus-visible:ring-bp-destructive/40 disabled:hover:bg-white disabled:hover:text-bp-destructive',
        /**
         * A bordered action. The ink is pinned to `--bp-ink` in every state
         * and the hover only tints the ground with that same ink, so no state
         * can hand the label to an ancestor's colour or to the 3.8:1 teal
         * `--accent` the registry reached for.
         *
         * The ground is `white/80` rather than `white/30`. Navy ink needs a
         * light ground under it, and at 30 % the navy frosted panel came
         * through hard enough to leave the label at 1.03:1 — a variant that is
         * legible only on the surfaces nobody happened to use it on. At 80 %
         * it carries its own contrast anywhere (9.0:1 on the navy panel,
         * 12.7:1 on white) and still reads as translucent.
         */
        outline:
          'border-surf-gray text-bp-ink border bg-white/80 shadow-xs hover:bg-surf-gray hover:text-bp-ink active:bg-surf-gray active:text-bp-ink focus-visible:ring-surf-blue/40 disabled:hover:bg-white/80',
        /**
         * `--secondary-foreground` is white on the ocean theme, so the
         * registry's `bg-white text-secondary-foreground` was white on white.
         * Navy ink, named outright.
         */
        secondary: 'text-bp-ink bg-white shadow-xs hover:bg-surf-gray active:bg-surf-gray focus-visible:ring-surf-blue/40',
        /**
         * The only variant that inherits its ink, and it keeps inheriting it
         * in every state — a ghost sits on surfaces as different as the white
         * image sheet and the navy video player, and the one safe thing it can
         * do is never change the colour it was given. The hover ground is the
         * same neutral wash the sheets' close button uses.
         */
        ghost: 'hover:bg-black/5 active:bg-black/10',
        link: 'text-surf-blue underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-2 text-xs',
        default: 'h-9 px-3 py-1 text-base sm:text-sm',
        lg: 'h-10 px-4 py-2 sm:h-9 ',
        icon: 'size-9 sm:size-8',
        /** The one obvious primary action on a screen: 48 px tall, full width. */
        xl: 'h-12 w-full rounded-full px-6 text-base font-bold',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';

  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { Button, buttonVariants };
